import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Flame, Play, Star } from 'lucide-react';
import { collection, query, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Link } from 'react-router-dom';

interface Oyun {
  id: string;
  name: string;
  imageUrl: string;
  provider: string;
  rating: number;
  playCount: number;
  order?: number;
  isActive?: boolean;
  isPopular?: boolean;
  gameUrl?: string;
}

const PopulerOyunlar: React.FC = () => {
  const [oyunlar, setOyunlar] = useState<Oyun[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Tüm oyunları çek, sonra client tarafında filtrele
    const oyunlarQuery = query(
      collection(db, 'populerOyunlar'),
      limit(50)
    );

    const unsubscribe = onSnapshot(oyunlarQuery, (snapshot) => {
      const allData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Oyun[];

      // Client tarafında filtreleme ve sıralama
      const filteredData = allData
        .filter(oyun => oyun.isActive !== false && oyun.isPopular !== false)
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
        .slice(0, 8);

      setOyunlar(filteredData);
      setLoading(false);
    }, () => {
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Demo veriler
  const demoOyunlar: Oyun[] = [
    { id: '1', name: 'Sweet Bonanza', imageUrl: 'https://via.placeholder.com/200x267/6366f1/ffffff?text=Sweet+Bonanza', provider: 'Pragmatic Play', rating: 4.8, playCount: 125000 },
    { id: '2', name: 'Gates of Olympus', imageUrl: 'https://via.placeholder.com/200x267/8b5cf6/ffffff?text=Gates+of+Olympus', provider: 'Pragmatic Play', rating: 4.9, playCount: 98000 },
    { id: '3', name: 'Big Bass Bonanza', imageUrl: 'https://via.placeholder.com/200x267/ec4899/ffffff?text=Big+Bass', provider: 'Pragmatic Play', rating: 4.7, playCount: 87000 },
    { id: '4', name: 'Starlight Princess', imageUrl: 'https://via.placeholder.com/200x267/f59e0b/ffffff?text=Starlight', provider: 'Pragmatic Play', rating: 4.6, playCount: 76000 },
    { id: '5', name: 'Sugar Rush', imageUrl: 'https://via.placeholder.com/200x267/10b981/ffffff?text=Sugar+Rush', provider: 'Pragmatic Play', rating: 4.5, playCount: 65000 },
    { id: '6', name: 'The Dog House', imageUrl: 'https://via.placeholder.com/200x267/3b82f6/ffffff?text=Dog+House', provider: 'Pragmatic Play', rating: 4.4, playCount: 54000 },
    { id: '7', name: 'Fruit Party', imageUrl: 'https://via.placeholder.com/200x267/ef4444/ffffff?text=Fruit+Party', provider: 'Pragmatic Play', rating: 4.3, playCount: 43000 },
    { id: '8', name: 'Madame Destiny', imageUrl: 'https://via.placeholder.com/200x267/a855f7/ffffff?text=Madame+Destiny', provider: 'Pragmatic Play', rating: 4.2, playCount: 32000 },
  ];

  const displayData = oyunlar.length > 0 ? oyunlar : demoOyunlar;

  return (
    <div className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 border border-dark-700 h-full flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
            <Flame className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white">En Popüler Oyunlar</h2>
        </div>
        <Link to="/slotlar/populer" className="text-primary-400 hover:text-primary-300 text-sm">
          Tümünü Gör →
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 flex-1">
        {loading ? (
          <>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="aspect-[3/4] bg-dark-700/50 rounded-xl animate-pulse" />
            ))}
          </>
        ) : (
          displayData.map((oyun, index) => (
            <motion.div
              key={oyun.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="group relative aspect-[3/4] rounded-xl overflow-hidden bg-dark-800 border border-dark-700 hover:border-primary-500/50 transition-all duration-300 cursor-pointer"
              onClick={() => oyun.gameUrl && window.open(oyun.gameUrl, '_blank')}
            >
              {/* Game Image */}
              <img
                src={oyun.imageUrl}
                alt={oyun.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />

              {/* Play Button - Hover */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-14 h-14 rounded-full bg-primary-500 flex items-center justify-center shadow-lg shadow-primary-500/50 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                  <Play className="w-6 h-6 text-white ml-1" />
                </div>
              </div>

              {/* Game Info */}
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <h3 className="text-sm font-semibold text-white truncate mb-1">
                  {oyun.name}
                </h3>
                <p className="text-xs text-gray-400 truncate flex items-center">
                  <Star className="w-3 h-3 text-yellow-500 mr-1" />
                  {oyun.provider}
                </p>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default PopulerOyunlar;
